<template>
  <side-panel-inner
    :headerTitle="`Edit ${$store.state.documents.docType.singular} version`"
    class="p-document-create"
  >
    <template slot="actions">
      <base-button primary="true" @click="onSubmit" iconBefore="pencil"
        >Publish</base-button
      >
    </template>

    <document-meta v-if="formData" :document="formData"></document-meta>

    <hr />

    <form action v-if="!loading">
      <p>Select the fields you wish to update below</p>
      <p>
        <strong>NOTE:</strong> If changes are made to the ‘Attached file’ you
        must add a new version number to the Policy
      </p>
      <accordion
        title="Policy Title"
        :subtitle="formData.current_version.title"
        expandLink="edit"
      >
        <form-field
          full-width
          name="document_title"
          v-model="formData.current_version.title"
          v-validate="{ required: true, min: 5 }"
          data-vv-as="title"
          :error="errors.first('document_title')"
          :max-length="150"
          :show-counter="true"
          required
        ></form-field>
      </accordion>

      <accordion
        title="Attached File"
        :subtitle="formData.current_version.file.name"
        expandLink="edit"
        v-if="formData.current_version.file"
      >
        <!-- File Upload -->
        <form-field
          label="File upload"
          name="document_file"
          helper-text="Upload your document"
          required
        >
          <file-upload
            v-model="formData.current_version.file"
            :fileTypes="documentFileTypes"
            name="document_file"
            :resource="docType.singular"
            @uploaded="onFileUploaded"
            :docType="docType"
            v-validate="{ required: true }"
            data-vv-as="file upload"
            :error="errors.first('document_file')"
            :resetOnLoad="false"
            :bypassDuplicate="true"
          ></file-upload>
        </form-field>
      </accordion>

      <accordion
        title="Web Form"
        :subtitle="formData.current_version.external_url"
        expandLink="edit"
        v-if="formData.current_version.external_url"
      >
        <form-field
          full-width
          name="external_url"
          v-model="formData.current_version.external_url"
          v-validate="{required: true, min: 5}"
          data-vv-as="title"
          :error="errors.first('external_url')"
        ></form-field>
      </accordion>

      <accordion
        title="Review Date"
        :subtitle="
          formData.current_version.review_date
            ? $dayjs(formData.current_version.review_date).format(
                'DD / MM / YYYY'
              )
            : ''
        "
        expandLink="edit"
      >
        <form-datepicker
          small
          v-model="formData.current_version.review_date"
          name="document_review_date"
          v-validate="{ required: true }"
          data-vv-as="review date"
          id="document_review_date"
          format="DD / MM / YYYY"
          :error="errors.first('document_review_date')"
        ></form-datepicker>
      </accordion>

      <accordion
        title="Responsible Users"
        :subtitle="userString"
        expandLink="edit"
      >
        <div class="row">
          <div class="col-xs-12">
            <form-label small required>Primary</form-label>
            <user-select
              :users="users"
              v-model="formData.primary_user"
              id="document_primary_user"
              name="document_primary_user"
              v-validate="{ required: true }"
              data-vv-as="primary user"
              :error="errors.first('document_primary_user')"
              :permissions="permissions"
            ></user-select>
          </div>
          <div class="col-xs-12">
            <form-label small required>Secondary</form-label>
            <user-select
              :users="users"
              v-model="formData.secondary_user"
              id="document_secondary_user"
              name="document_secondary_user"
              v-validate="{ required: true, is_not: formData.primary_user }"
              data-vv-as="secondary user"
              :error="errors.first('document_secondary_user')"
              :permissions="permissions"
            ></user-select>
          </div>
        </div>
      </accordion>

      <accordion title="Version" :subtitle="versionNumber" expandLink="edit">
        <!-- Major or Minor Version -->
        <form-options
          label="What type of version?"
          v-model="newVersion"
          name="new_version"
          :options="versionOptions"
          data-vv-as="new version"
          v-validate="{ required: newFileId ? true : false }"
          :error="errors.first('new_version')"
        ></form-options>
      </accordion>

      <message type="error" v-if="showValidationMessage || showVersionError">
        <h3>Form Invalid</h3>
        <p>The form is invalid, please check and try again.</p>
        <p v-if="showVersionError">
          You have changed the file, but have not updated the version number.
        </p>

        <ul v-if="errors">
          <li v-for="error in errors.items">{{ error.msg }}</li>
        </ul>
      </message>
    </form>

    <loading v-else></loading>
  </side-panel-inner>
</template>

<script>
import FileUpload from '@/components/FileUpload'
import Accordion from '@/components/Containers/BaseAccordion'
import UserSelect from '@/components/UserSelect'

import { mapState } from 'vuex'

export default {
  name: 'DocumentEdit',

  components: {
    FileUpload,
    Accordion,
    UserSelect
  },

  data() {
    return {
      loading: true,
      formData: null,
      newVersion: null,
      newFileId: null,
      showValidationMessage: false,
      permissions: ['add_policies'],
      showVersionError: false,
      creating: false
    }
  },

  methods: {
    onSubmit() {
      this.loading = true

      this.$validator.validate().then(result => {
        if (!result) {
          this.loading = false
          this.showValidationMessage = true
        } else {
          /**
           * If the file has changed, we need to check that the version has changed...
           */
          if (
            this.newFileId &&
            this.newFileId !== this.formData.current_version.file.id &&
            (!this.newVersion ||
              this.newVersion.toFixed(1) ===
                parseInt(this.formData.current_version.version).toFixed(1))
          ) {
            this.loading = false
            this.showVersionError = true
            this.showValidationMessage = true
            return
          }
          this.showValidationMessage = false
          this.updateDocument()
        }
      })
    },

    updateDocument() {
      const vm = this
      const formData = this.formData

      this.creating = true

      const tagIds = []

      if (formData.tags) {
        formData.tags.forEach(tag => {
          if (tag.id) {
            tagIds.push(tag.id)
          } else {
            tagIds.push(tag.name)
          }
        })
      }

      const documentData = {
        title: formData.current_version.title,
        description: 'Description Text',
        primary_category_id: formData.primary_category.id,
        primary_user_id: formData.primary_user.id,
        secondary_user_id: formData.secondary_user.id,
        tags: formData.tags,
        new_version: this.newVersion,
        newFileId: this.newFileId,
        id: formData.id,
        current_version: formData.current_version,
        eqia_number: formData.eqia_number,
        ratified_group_id: formData.ratified_group_id
      }

      // console.log("TCL: updateDocument -> documentData", documentData)
      // return;
      if (formData.categories) {
        documentData.category_ids = formData.categories.map(cat => cat.id) //we only need ids
      }
      const skipVersion = false;
      this.$store
        .dispatch('documents/update', {documentData, skipVersion})
        .then(() => {
          // If we have a review date, we need to update the version
          const dateArgs = {
            policyId: formData.id,
            versionId: formData.current_version.id,
            payload: {
              review_date: formData.current_version.review_date
            }
          }
          this.$store.dispatch('documentVersions/update', dateArgs)
          setTimeout(() => {
            vm.$store.dispatch('CLOSE_PANEL')
          }, 1000)
        })
        .catch(err => {
          this.showValidationMessage = true
        })
    },

    onFileUploaded(fileData) {
      this.newFileId = fileData.id
    },

    async retrieveDocument() {
      const vm = this
      const documentId = this.$route.params.id
      let document

      try {
        document = await this.$store.dispatch('documents/read', documentId)
        this.formData = document

        if (!this.formData.current_version || this.$route.params.versionId !== this.formData.current_version.id) {
          const { versionId } = this.$route.params

          const version = await this.$store.dispatch('documents/getVersion', {
            documentId,
            versionId
          })
          this.formData.current_version = version
        }
        vm.loading = false

        /**
         * Fake the file as uploaded
         */
        this.$store.commit(
          'files/setLocalFile',
          this.formData.current_version.file
        )
        this.$store.commit('files/setUploadPercent', 100)
        this.$store.commit(
          'files/setUploadedFile',
          this.formData.current_version.file
        )
        // this.newFileId = this.formData.current_version.file.id
      } catch (err) {
        vm.loading = false
      }
    },
    addTag(newTag) {
      const formTag = {
        id: null,
        name: newTag
      }
      if (!this.formData.tags) {
        this.formData.tags = []
      }
      this.formData.tags.push(formTag)
      this.$store.dispatch('tags/add', newTag)
    }
  },

  computed: {
    ...mapState('categories', ['categories']),
    ...mapState({
      users: state => state.users.collection
    }),
    ...mapState({
      tags: state => state.tags.collection
    }),
    /**
     * Format groups to format for dropdown
     */
    ratifiedGroupOptions() {
      return ratifiedGroupOptions.map(item => {
        return { ...item, name: item.label }
      })
    },
    /**
     * Get the current ratified group string
     */
    ratifiedGroup: {
      get() {
        const group = this.ratifiedGroupOptions.filter(
          group => group.id === this.formData.ratified_group_id
        )

        if (group.length < 1) {
          return {}
        }

        return { id: group[0].id, name: group[0].name }
      },
      set(newVal) {
        this.formData.ratified_group_id = newVal.id
      }
    },

    /**
     * Current version number in float format
     */
    versionNumber() {
      return parseFloat(this.formData.current_version.version)
        .toPrecision(2)
        .toString()
    },
    /**
     * comma-separated list of categories
     */
    categoryString() {
      let primary = ''
      if (this.formData.primary_category) {
        primary = this.formData.primary_category.name
      }
      const secondaries = this.formData.categories

      let returnString = primary

      if (secondaries) {
        secondaries.forEach(cat => {
          returnString += `, ${cat.name}`
        })
      }

      return returnString
    },
    /**
     * comma-separated list of tags
     */
    tagString() {
      const tags = this.formData.tags

      if (!tags || tags.length < 1) {
        return '(No tags added)'
      }

      let returnString = tags[0].name

      tags.forEach((tag, i) => {
        if (i > 0) {
          returnString += `, ${tag.name}`
        }
      })

      return returnString
    },
    /**
     * comma-separated list of users
     */
    userString() {
      let primary = ''
      let secondary = ''
      if (this.formData.primary_user) {
        primary = this.formData.primary_user.name
          ? this.formData.primary_user.name
          : this.formData.primary_user.email
      }
      if (this.formData.secondary_user) {
        secondary = this.formData.secondary_user.name
          ? `, ${this.formData.secondary_user.name}`
          : `, ${this.formData.secondary_user.email}`
      }

      return `${primary} ${secondary}`
    },
    versionOptions() {
      const majorVersion = parseInt(this.formData.current_version.version) + 1
      const minorVersion =
        parseFloat(this.formData.current_version.version) + 0.1

      return [
        {
          label: `Major (v${majorVersion}.0)`,
          value: majorVersion,
          content: ''
        },
        {
          label: `Minor (v${minorVersion.toFixed(1)})`,
          value: minorVersion,
          content: ''
        }
      ]
    },

    documentFileTypes() {
      if (this.docType.singular == 'policy') {
        return ['pdf','xls','xlsx']
      }

      return null
    },

    docType() {
      return this.$store.state.documents.docType
    }
  },

  beforeCreate() {
    this.$store.dispatch('categories/list')
    this.$store.dispatch('users/list')
  },

  beforeMount() {
    // so that the background isn't empty...
    if (
      !this.$store.state.documents[this.$store.state.documents.docType.plural]
    ) {
      this.$store.dispatch('documents/list')
    }
  },

  created() {
    this.retrieveDocument()
    this.$store.dispatch('tags/list')
  }
}
</script>
